import { Location } from '@angular/common';
import { Component, HostListener, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { AuthenticationContants } from 'src/app/constants/authentication-constants';
import { ProjectName } from 'src/app/constants/project-name-constants';
import { ConceptModel } from 'src/app/models/management/concept.model';
import { WindowService } from 'src/app/services/authentication/window.service';
import { ConfirmDialogService } from 'src/app/services/confirm-dialog/confirm-dialog.service';
import { HeaderService } from 'src/app/services/header/header.service';
import { ConceptService } from 'src/app/services/management/concept.service';
import { ToastHandlerService } from 'src/app/services/toast/toast-handler.service';

@Component({
  selector: 'app-register-concept',
  templateUrl: './register-concept.component.html',
  styleUrls: ['./register-concept.component.scss'],
  standalone: false
})
export class RegisterConceptComponent implements OnInit {

  conceptContract: ConceptModel = new ConceptModel();
  conceptId: number = 0;
  disabledForm: boolean = false;
  formConcept: FormGroup = new FormGroup({});
  isOpened: string | undefined = undefined;
  listTypeConcepts: any[] = [];
  nameWindow: string = 'Captura Conceptos';
  permissionUpdatable: boolean = false;
  projectName = ProjectName.projectName;
  url: string = 'concept';

  constructor(private confirmDialogService: ConfirmDialogService,
    private activatedRoute: ActivatedRoute,
    private conceptService: ConceptService,
    private formBuilder: FormBuilder,
    private headerService: HeaderService,
    private location: Location,
    private title: Title,
    private toastHandlerService: ToastHandlerService,
    private windowService: WindowService) {
    this.validateForm(new ConceptModel());
  }

  async ngOnInit() {
    this.headerService.setTitle(this.nameWindow);
    this.title.setTitle(`${this.nameWindow} - ${this.projectName}`);
    this.activatedRoute.params.subscribe(params => {
      this.conceptId = Object.keys(params).length > 0 ? params?.id : null;
    });
    await this.getPermission();
    this.handleInitialState();
  }

  @HostListener('window:beforeunload')
  beforeUnloadHandler() {
    this.ngOnDestroy();
  }

  ngOnDestroy() {
    const windowsOpenedJson = localStorage.getItem(AuthenticationContants.keyWindowsOpenedLocalStorage);
    if (windowsOpenedJson) {
      const windowsOpened: Array<string> = JSON.parse(windowsOpenedJson);
      const window = windowsOpened.findIndex(x => x === this.url);
      if (window != -1) {
        windowsOpened.splice(window, 1);
        localStorage.setItem(AuthenticationContants.keyWindowsOpenedLocalStorage, JSON.stringify(windowsOpened));
      }
    } else {
      localStorage.clear();
    }
  }

  async changeConcept(code: number) {
    const typeConceptId = this.formConcept.get("typeConceptId")?.value;
    if (!code || code <= 0) {
      this.toastHandlerService.showToast('warning', 'El código del concepto no es válido.');
      return;
    }
    if (!typeConceptId) {
      this.toastHandlerService.showToast('warning', `Debe seleccionar primero el tipo concepto.`);
      return;
    }
    await this.getConceptByCode(typeConceptId, code);
  }

  createNewRegister() {
    if (!this.permissionUpdatable) {
      this.toastHandlerService.showToast('error', `El usuario no tiene permisos para crear conceptos.
        Comuníquese con el administrador del sistema.`);
      return;
    }
    this.conceptId = 0;
    this.conceptContract = new ConceptModel();
    this.validateForm(this.conceptContract);
    this.updateFormPermision();
  }

  changeTypeConcept(typeConceptId: number) {
    this.conceptId = 0;
    this.conceptContract = new ConceptModel();
    this.conceptContract.typeConceptId = typeConceptId;
    this.validateForm(this.conceptContract);
  }

  async delete() {
    if (!this.permissionUpdatable) {
      this.toastHandlerService.showToast('error', `No tiene permisos para eliminar conceptos.
          Comuníquese con el administrador del sistema.`);
      return;
    }
    if (await this.confirmDialogService.confirm('¿Realmente desea eliminar el concepto?')) {
      try {
        await firstValueFrom(this.conceptService.deleteConcept(this.conceptContract.conceptId));
        this.toastHandlerService.showToast('success', 'El concepto fue eliminado exitosamente.');
        this.createNewRegister();
      } catch (error) {
        console.error('Error al eliminar el concepto.', error);
      }
    }
  }

  async getButtonClick(buttonClick: any) {
    const typeConceptId = this.formConcept.get("typeConceptId")?.value;
    const code = this.formConcept.get("code")?.value;
    if (!typeConceptId || typeConceptId <= 0) {
      this.toastHandlerService.showToast('warning', 'Seleccione un tipo concepto.');
      return;
    }
    this.handleCostCenterNavigation(buttonClick, typeConceptId, code)
  }

  async getConceptById() {
    try {
      this.conceptContract = await firstValueFrom(this.conceptService.getConceptById(this.conceptId));
      this.validateForm(this.conceptContract);
      this.updateFormPermision();
    } catch (error) {
      console.error('Error al obtener la concepto por id.', error);
    }
  }

  async getConceptByCode(typeConceptId: number, code: number) {
    try {
      const response = await firstValueFrom(this.conceptService.getConceptByCode(typeConceptId, code));
      this.conceptId = response.conceptId;
      this.conceptContract = response;
      this.validateForm(this.conceptContract);
    } catch (error) {
      console.error('Error al obtener el concepto por codigo.', error);
    }
  }

  async getListTypeConcepts() {
    try {
      this.listTypeConcepts = await firstValueFrom(this.conceptService.getAllTypeConceptsForSelect());
    } catch (error) {
      console.error('Error al obtner la lista de tipos de concepto.', error);
    }
  }

  async getPermission() {
    const windowsOpenedJson = localStorage.getItem(AuthenticationContants.keyWindowsOpenedLocalStorage);
    if (windowsOpenedJson) {
      const windowsOpened: Array<string> = JSON.parse(windowsOpenedJson);
      this.isOpened = windowsOpened.find(x => x === this.url);
      windowsOpened.push(this.url);
      localStorage.setItem(AuthenticationContants.keyWindowsOpenedLocalStorage, JSON.stringify(windowsOpened));
      if (this.isOpened) {
        this.toastHandlerService.showToast('error', `No se permite tener dos ventanas/pestañas de ${this.nameWindow} abiertas.`);
      }
    } else {
      localStorage.clear();
    }
    await Promise.all([
      this.getPermissionProfileByUrl(),
      this.getListTypeConcepts()
    ]);
  }

  async getPermissionProfileByUrl() {
    try {
      this.permissionUpdatable = await firstValueFrom(this.windowService.getPermissionProfileByUrl(this.url));
    } catch (error) {
      console.error('Error al cargar permisos.', error);
    }
  }

  async handleInitialState() {
    if (this.conceptId !== null && this.conceptId !== 0) {
      this.getConceptById();
    } else {
      this.createNewRegister();
    }
  }

  private async handleCostCenterNavigation(buttonClick: any, typeConceptId: number, code: number) {
    const actionMap: Record<string, () => Promise<ConceptModel>> = {
      first: () => firstValueFrom(this.conceptService.getFirstConceptByKeysAsync(typeConceptId)),
      previus: () => firstValueFrom(this.conceptService.getPreviusConceptByKeysAsync(typeConceptId, code)),
      next: () => firstValueFrom(this.conceptService.getNextConceptByKeysAsync(typeConceptId, code)),
      last: () => firstValueFrom(this.conceptService.getLastConceptByKeysAsync(typeConceptId)),
    };
    const action = actionMap[buttonClick.target.id];
    if (!action) {
      this.conceptId = 0;
      this.conceptContract = new ConceptModel();
      this.conceptContract.typeConceptId = typeConceptId;
      return;
    }
    try {
      const concept = await action();
      this.conceptId = concept.conceptId;
      this.conceptContract = concept;
      this.validateForm(this.conceptContract);
      this.location.replaceState(`/${this.url}/register/${this.conceptId}`);
    } catch (error) {
      this.conceptContract = new ConceptModel();
      this.conceptContract.conceptId = 0;
      this.conceptContract.typeConceptId = typeConceptId;
      this.conceptContract.code = code;
      console.error(`Error al buscar el concepto (${buttonClick.target.id}).`, error);
    }
  }

  async send() {
    this.validateFormControls();
    if (this.formConcept.valid) {
      const modelConcept = this.formConcept.getRawValue() as ConceptModel;
      if (modelConcept.alterCode == '') modelConcept.alterCode = null;
      if (modelConcept.alterCode2 == '') modelConcept.alterCode2 = null;
      if (modelConcept.typeConceptId == null || modelConcept.typeConceptId == 0) {
        this.toastHandlerService.showToast('warning', `Debe seleccionar primero el tipo concepto.`);
        return;
      }
      if (this.conceptId == 0) {
        try {
          const response = await firstValueFrom(this.conceptService.createConcept(modelConcept));
          this.conceptId = response.conceptId;
          this.conceptContract = response;
          this.validateForm(this.conceptContract);
          this.toastHandlerService.showToast('success', `El concepto fue creado exitosamente.`);
        } catch (error) {
          console.error('Error al crear el concepto.', error);
        }
      } else {
        this.conceptContract.typeConceptId = modelConcept.typeConceptId
        this.conceptContract.code = modelConcept.code
        this.conceptContract.alterCode = modelConcept.alterCode
        this.conceptContract.alterCode2 = modelConcept.alterCode2
        this.conceptContract.name = modelConcept.name
        try {
          const response = await firstValueFrom(this.conceptService.updateConcept(this.conceptContract));
          this.conceptContract = response;
          this.validateForm(this.conceptContract);
          this.toastHandlerService.showToast('success', `El concepto fue modificado exitosamente.`);
        } catch (error) {
          console.error('Error al modificar el concepto.', error);
        }
      }
    } else {
      this.showValidationMessages();
    }
  }

  showValidationMessages() {
    const controlMessages: Record<string, Record<string, string>> = {
      typeConceptId: {
        required: 'El campo Tipo de concepto es obligatorio.',
        min: 'Debes seleccionar un tipo de concepto válido.',
      },
      code: {
        required: 'El campo Código es obligatorio.',
      },
      alterCode: {
        maxlength: 'El Código alterno 1 no puede superar los 10 caracteres.',
      },
      alterCode2: {
        maxlength: 'El Código alterno 2 no puede superar los 10 caracteres.',
      },
      name: {
        required: 'El campo Nombre es obligatorio.',
        maxlength: 'El Nombre no puede superar los 70 caracteres.',
      },
    };

    Object.keys(controlMessages).forEach((controlName) => {
      const formControl = this.formConcept.get(controlName);
      if (formControl && formControl.invalid && formControl.errors) {
        const messages = controlMessages[controlName];
        Object.keys(formControl.errors).forEach((errorKey) => {
          const message = messages[errorKey];
          if (message) {
            this.toastHandlerService.showToast('warning', message);
          }
        });
      }
    });
  }

  updateFormPermision() {
    if (this.permissionUpdatable) {
      this.disabledForm = false;
      const codeControl = this.formConcept.controls['code'];
      setTimeout(() => {
        this.formConcept.enable()
        this.conceptId !== 0 ? codeControl.enable() : codeControl.disable()
      });
    } else {
      this.disabledForm = true;
      setTimeout(() => this.formConcept.disable());
      setTimeout(() => this.formConcept.controls["code"].enable());
    }
  }

  validateForm(concept: ConceptModel) {
    this.formConcept = this.formBuilder.group({
      typeConceptId: [concept.typeConceptId, [Validators.required, Validators.min(1)]],
      code: [concept.code, [Validators.required]],
      alterCode: [concept.alterCode, [Validators.maxLength(10)]],
      alterCode2: [concept.alterCode2, [Validators.maxLength(10)]],
      name: [concept.name, [Validators.required, Validators.maxLength(70)]]
    });
    const codeControl = this.formConcept.controls['code'];
    setTimeout(() => this.conceptId !== 0 ? codeControl.enable() : codeControl.disable());
  }

  validateFormControls() {
    Object.keys(this.formConcept.controls).forEach((key) => {
      const control = this.formConcept.get(key);
      if (control && control.invalid) {
        console.warn(
          `${key} es inválido`,
          control.errors,
          'value:',
          control.value
        );
      }
    });
  }
}
